import React, { useEffect, useRef, useState } from 'react';
import { Stage, SubscribeType, StageEvents } from 'amazon-ivs-web-broadcast';
import { logger } from '../utils/logger';
import { ivsService } from '../services/ivsService';
import './LiveStreamPlayer.css';

interface LiveStreamPlayerProps {
  streamId: string; // Stream ID to subscribe to
  mediaType?: 'video' | 'audio'; // Support both video and audio-only streams
  isLive?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  onError?: (error: string) => void;
  onStreamEnd?: () => void;
}

const LiveStreamPlayer: React.FC<LiveStreamPlayerProps> = ({
  streamId,
  mediaType = 'audio',
  isLive = true,
  autoPlay = true,
  muted = false,
  onError,
  onStreamEnd
}) => {
  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
  const stageRef = useRef<Stage | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(muted);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState | null>(null);

  // Connect to WHEP stream
  useEffect(() => {
    let isMounted = true;

    const connectToStream = async () => {
      try {
        logger.info(`🎧 LiveStreamPlayer: Connecting to stream ${streamId} via IVS Stage...`);
        setLoading(true);
        setError(null);

        // Request SUBSCRIBE token from backend
        const tokenResponse = await ivsService.createParticipantToken(streamId, 'SUBSCRIBE');

        if (!isMounted) return;

        const { token } = tokenResponse;

        if (!token) {
          throw new Error('Invalid token response from server');
        }

        logger.info('✅ Received SUBSCRIBE token:', {
          streamId,
          participantId: tokenResponse.participantId
        });

        // Create IVS Stage with subscription strategy
        const strategy = {
          stageStreamsToPublish() {
            return []; // Subscriber doesn't publish
          },
          shouldPublishParticipant() {
            return false;
          },
          shouldSubscribeToParticipant() {
            return SubscribeType.AUDIO_VIDEO; // Subscribe to all tracks
          }
        };

        logger.info('🎭 Creating IVS Stage for subscription...');
        const stage = new Stage(token, strategy);

        // Handle incoming remote participant streams
        stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (participant: any, streams: any[]) => {
          logger.info(`📥 Received streams from participant ${participant.id}`, { streamCount: streams.length });

          if (streams.length > 0 && mediaRef.current && isMounted) {
            try {
              // Create a new MediaStream and add tracks from all StageStreams
              const mediaStream = new MediaStream();

              streams.forEach((stageStream: any) => {
                logger.info('🎬 Processing stream', { stageStream });

                // StageStream has a mediaStreamTrack property containing the actual MediaStreamTrack
                if (stageStream.mediaStreamTrack) {
                  logger.info(`✅ Adding track: ${stageStream.mediaStreamTrack.kind}`);
                  mediaStream.addTrack(stageStream.mediaStreamTrack);
                }
              });

              logger.info('📡 Setting srcObject with MediaStream', { trackCount: mediaStream.getTracks().length });
              mediaRef.current.srcObject = mediaStream;
              logger.info('✅ Attached stream to media element');
              setLoading(false);

              if (autoPlay) {
                mediaRef.current.play().catch(err => {
                  logger.error('Error auto-playing media', err);
                  setIsPlaying(false);
                });
              }
            } catch (err) {
              logger.error('❌ Error processing streams', err);
              setError('Failed to process audio stream');
              if (onError) onError('Failed to process audio stream');
            }
          }
        });

        // Handle connection state changes
        stage.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, (state: string) => {
          logger.info(`🔄 Stage connection state: ${state}`);
          setConnectionState(state as RTCPeerConnectionState);

          if (state === 'failed' || state === 'disconnected') {
            if (isMounted) {
              const errorMsg = 'Stream connection lost';
              setError(errorMsg);
              if (onError) onError(errorMsg);
            }
          }
        });

        // Handle participant leaving
        stage.on(StageEvents.STAGE_PARTICIPANT_LEFT, (participant: any) => {
          logger.info(`👋 Participant left: ${participant.id}`);
          if (isMounted && onStreamEnd) {
            onStreamEnd();
          }
        });

        stageRef.current = stage;

        // Join the stage
        logger.info('📡 Joining IVS Stage as subscriber...');
        await stage.join();

        logger.info('✅ IVS Stage subscription established successfully');
      } catch (err: any) {
        logger.error('❌ Failed to connect to IVS Stage', err);
        if (isMounted) {
          const errorMsg = err.message || 'Failed to connect to stream';
          setError(errorMsg);
          setLoading(false);
          if (onError) onError(errorMsg);
        }
      }
    };

    connectToStream();

    // Cleanup on unmount
    return () => {
      logger.info('🧹 LiveStreamPlayer unmounting, cleaning up IVS Stage connection...');
      isMounted = false;

      if (stageRef.current) {
        stageRef.current.leave();
        stageRef.current = null;
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
      const mediaElement = mediaRef.current;
      if (mediaElement && mediaElement.srcObject) {
        const stream = mediaElement.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        mediaElement.srcObject = null;
      }
    };
  }, [streamId, autoPlay, onError, onStreamEnd]);

  // Media event handlers
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      if (onStreamEnd) onStreamEnd();
    };
    const handleTimeUpdate = () => setCurrentTime(media.currentTime);
    const handleDurationChange = () => setDuration(media.duration);
    const handleError = () => {
      const errorMsg = `${mediaType === 'audio' ? 'Audio' : 'Video'} playback error`;
      setError(errorMsg);
      if (onError) onError(errorMsg);
    };

    media.addEventListener('play', handlePlay);
    media.addEventListener('pause', handlePause);
    media.addEventListener('ended', handleEnded);
    media.addEventListener('timeupdate', handleTimeUpdate);
    media.addEventListener('durationchange', handleDurationChange);
    media.addEventListener('error', handleError);

    return () => {
      media.removeEventListener('play', handlePlay);
      media.removeEventListener('pause', handlePause);
      media.removeEventListener('ended', handleEnded);
      media.removeEventListener('timeupdate', handleTimeUpdate);
      media.removeEventListener('durationchange', handleDurationChange);
      media.removeEventListener('error', handleError);
    };
  }, [onError, onStreamEnd, mediaType]);

  const togglePlay = () => {
    if (!mediaRef.current) return;

    if (isPlaying) {
      mediaRef.current.pause();
    } else {
      mediaRef.current.play().catch(err => {
        logger.error('Error playing media', err);
      });
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (mediaRef.current) {
      mediaRef.current.volume = newVolume;
      if (newVolume > 0 && isMuted) {
        setIsMuted(false);
        mediaRef.current.muted = false;
      }
    }
  };

  const toggleMute = () => {
    if (!mediaRef.current) return;
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    mediaRef.current.muted = newMuted;
  };

  const toggleFullscreen = () => {
    const media = mediaRef.current;
    if (!media || mediaType === 'audio') return; // No fullscreen for audio

    if (!document.fullscreenElement) {
      media.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        logger.error('Error entering fullscreen', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mediaRef.current || isLive) return;
    const newTime = parseFloat(e.target.value);
    mediaRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="player-error">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2"/>
          <path d="M24 14V26M24 32H24.02" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <p>{error}</p>
        <p className="connection-state">Connection: {connectionState || 'unknown'}</p>
      </div>
    );
  }

  return (
    <div className="live-stream-player-container">
      {loading && (
        <div className="player-loading">
          <div className="loading-spinner">
            <div className="spinner-circle"></div>
            <div className="spinner-circle"></div>
            <div className="spinner-circle"></div>
          </div>
          <p>Connecting via IVS Real-Time...</p>
          {connectionState && <p className="connection-state">{connectionState}</p>}
        </div>
      )}

      <div className={`${mediaType}-wrapper`}>
        {mediaType === 'audio' ? (
          <audio
            ref={mediaRef as React.RefObject<HTMLAudioElement>}
            className="audio-player"
            muted={isMuted}
            autoPlay={autoPlay}
          />
        ) : (
          <video
            ref={mediaRef as React.RefObject<HTMLVideoElement>}
            className="video-player"
            playsInline
            muted={isMuted}
            autoPlay={autoPlay}
          />
        )}

        {isLive && (
          <div className="live-indicator">
            <span className="pulse-dot"></span>
            LIVE
          </div>
        )}

        {/* Video Controls */}
        <div className="video-controls">
          {/* Progress Bar (only for non-live streams) */}
          {!isLive && duration > 0 && (
            <div className="progress-bar-wrapper">
              <input
                type="range"
                min="0"
                max={duration}
                value={currentTime}
                onChange={handleSeek}
                className="progress-bar"
              />
              <div
                className="progress-bar-fill"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
            </div>
          )}

          <div className="controls-bottom">
            {/* Play/Pause Button */}
            <button onClick={togglePlay} className="control-button play-button">
              {isPlaying ? (
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <rect x="6" y="5" width="4" height="14" rx="1"/>
                  <rect x="14" y="5" width="4" height="14" rx="1"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            {/* Time Display */}
            {!isLive && (
              <div className="time-display">
                <span>{formatTime(currentTime)}</span>
                <span>/</span>
                <span>{formatTime(duration)}</span>
              </div>
            )}

            <div className="controls-right">
              {/* Volume Control */}
              <div className="volume-control">
                <button onClick={toggleMute} className="control-button">
                  {isMuted || volume === 0 ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      <path d="M23 9l-6 6M17 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  ) : volume > 0.5 ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      <path d="M15.54 8.46a5 5 0 010 7.07M18.36 5.64a9 9 0 010 12.73" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      <path d="M15.54 8.46a5 5 0 010 7.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="volume-slider"
                />
              </div>

              {/* Fullscreen Button (only for video) */}
              {mediaType === 'video' && (
                <button onClick={toggleFullscreen} className="control-button">
                  {isFullscreen ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveStreamPlayer;
